/**
 * useEmailLogin - Hook for email/password authentication within signup modal
 *
 * Handles email/password login flow for existing users who prefer traditional
 * authentication over OTP. After successful login, checks profile completeness
 * and billing status to determine next screen.
 */
import { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useState } from 'react';

import { CREATE_SMB_SETUP_INTENT_MUTATION, CREATE_TOKEN_MUTATION } from '@/auth/store/queries';
import { authSession } from '@/auth/utils/authSession';
import { handleCredentialsMissing } from '@/auth/utils/credentialsMissing';
import config from '@/config';
import {
  APIError,
  GraphQLClient,
  OtpMethod,
  UnauthorizedError,
  apiErrorMessage,
  basicAuth,
} from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { clearCache } from '@/utils';

import type { ProfileState, StripeState, UserData } from '../types';
import { mapBillingAccount } from '../utils/mapBillingAccount';
import { checkProfileCompleteness } from '../utils/profileCompleteness';

export interface MfaState {
  required: boolean;
  method: OtpMethod | null;
  maskedAddress: string | null;
}

export interface UseEmailLoginResult {
  token: string;
  user: UserData;
  profileState: ProfileState | null;
  stripeState: StripeState | null;
  needsPaymentSetup: boolean;
}

export interface UseEmailLoginOptions {
  onSuccess: (result: UseEmailLoginResult) => void;
  onError: (error: string) => void;
}

export const useEmailLogin = ({
  onSuccess,
  onError,
}: UseEmailLoginOptions): {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  error: string | null;
  isLoading: boolean;
  handleLogin: () => Promise<void>;
  // MFA state and handlers
  mfa: MfaState;
  otpCode: string;
  setOtpCode: (code: string) => void;
  handleMfaVerify: () => Promise<void>;
  handleMfaCancel: () => void;
  handleMfaResend: () => Promise<void>;
  mfaResendSuccess: boolean;
} => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // MFA state
  const [mfa, setMfa] = useState<MfaState>({
    required: false,
    method: null,
    maskedAddress: null,
  });
  const [otpCode, setOtpCode] = useState('');
  const [mfaResendSuccess, setMfaResendSuccess] = useState(false);

  /**
   * Shared post-authentication flow: fetch viewer, check profile, setup payment, call onSuccess.
   * Used by both handleLogin and handleMfaVerify after obtaining a token.
   */
  const processAuthenticatedUser = async (token: string) => {
    // Step 1: Authenticate: store token, fetch viewer, sync Redux
    const viewer = await authSession.authenticate(token);

    // Step 2: Extract user and billing information
    const user = viewer;
    const group = user.groups?.[0];
    const billingAccount = group?.billing_account;

    // Step 3: Check profile completeness
    const profileCheck = checkProfileCompleteness(user, group);

    // Step 4: Prepare user data
    const userData: UserData = {
      id: user.id,
      type: 'existing',
      name: user.profile?.name || `${user.profile?.first_name} ${user.profile?.last_name}`,
      company: group?.name || '',
      maskedAddress: '',
      billing: billingAccount ? mapBillingAccount(billingAccount) : null,
      groupId: group?.id,
    };

    // Step 5: Prepare profile state if incomplete
    let profileState: ProfileState | null = null;
    if (!profileCheck.isComplete) {
      profileState = {
        isPartial: true,
        missingFields: profileCheck.missingFields,
        existingData: profileCheck.existingData,
      };
    }

    // Step 6: Handle payment setup if no billing account
    let needsPaymentSetup = false;
    let stripeState: StripeState | null = null;

    if (!billingAccount) {
      needsPaymentSetup = true;

      // Create SetupIntent for existing authenticated user
      // Uses bearer auth from authSession - user details pulled from context
      const setupData = await authSession.createClient().mutate(CREATE_SMB_SETUP_INTENT_MUTATION, {
        input: {
          companyName: group?.name || undefined,
        },
      });

      // authClient.mutate() returns data directly, not wrapped in { data }
      if (setupData.createSMBSetupIntent) {
        stripeState = {
          clientSecret: setupData.createSMBSetupIntent.clientSecret,
          customerId: setupData.createSMBSetupIntent.stripeCustomerId,
        };
      }
    }

    // Step 7: Call success callback
    onSuccess({
      token,
      user: userData,
      profileState,
      stripeState,
      needsPaymentSetup,
    });
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Authenticate without redirect using basic auth
      const authClient = new GraphQLClient(config.apiUrl, basicAuth(email, password));

      // Use authClient.mutate() instead of authClient.client.mutate()
      // This ensures 401 errors are converted to UnauthorizedError
      const authData = await authClient.mutate(CREATE_TOKEN_MUTATION, {
        loginMeta: {
          method: 'password',
          ua: window.navigator.userAgent,
        },
      });

      // authClient.mutate() returns data directly, not wrapped in { data }
      const tokenData = authData.createToken;
      if (!tokenData) {
        throw new Error('Authentication failed');
      }

      await processAuthenticatedUser(tokenData.token);
    } catch (err: unknown) {
      const credentialsMissing = handleCredentialsMissing(err);
      let errorMessage: string | null = null;

      if (credentialsMissing) {
        errorMessage = credentialsMissing;
      }
      // Handle UnauthorizedError (401 responses from basic auth)
      else if (err instanceof UnauthorizedError) {
        switch (err.code) {
          case ERROR_CODES.AUTH_MFA_REQUIRED:
            // MFA is required - show OTP verification form
            setMfa({
              required: true,
              method: err.otpMethod || null,
              maskedAddress: err.otpMaskedAddress || null,
            });
            setIsLoading(false);
            // Don't set error or call onError - just show MFA form
            return;
          case ERROR_CODES.AUTH_INVALID_CREDENTIALS:
          case ERROR_CODES.AUTH_USER_NOT_FOUND:
            // Match legacy login page message exactly
            errorMessage = 'Invalid username or password';
            break;
          case ERROR_CODES.AUTH_LOCKED_FOR_INTERVAL:
            // Account temporarily locked due to too many failed attempts
            errorMessage = 'Too many login attempts. Please try again later.';
            break;
          default:
            // Unexpected 401 error code
            Sentry.captureException(err);
            errorMessage = err.message || 'Invalid username or password';
        }
      }
      // Handle ApolloError (network/GraphQL errors)
      else if (err instanceof ApolloError) {
        Sentry.captureException(err);
        if (err.networkError) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.graphQLErrors.length > 0) {
          // fetchViewer's errors arrive here, from the plain Apollo client
          errorMessage = apiErrorMessage(err, 'Authentication failed. Please try again.');
        } else {
          errorMessage = 'Service temporarily unavailable. Please try again.';
        }
      }
      // GraphQL errors: the server's text only when it carries a domain code (ENG-5831)
      else if (err instanceof APIError) {
        Sentry.captureException(err);
        errorMessage = apiErrorMessage(err, 'An unexpected error occurred. Please try again.');
      }
      // Handle generic errors
      else if (err instanceof Error) {
        Sentry.captureException(err);
        errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      }
      // Handle unknown error types
      else {
        Sentry.captureException(err);
        errorMessage = 'An unexpected error occurred. Please try again.';
      }

      if (errorMessage) {
        setError(errorMessage);
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
      clearCache('smb_consultation_data');
    }
  };

  /**
   * Verify MFA code and complete authentication
   * Uses basic auth with OTP header to authenticate
   */
  const handleMfaVerify = async () => {
    setIsLoading(true);
    setError(null);
    setMfaResendSuccess(false);

    try {
      // Create client with basic auth + OTP code
      const authClient = new GraphQLClient(config.apiUrl, basicAuth(email, password), {
        otp: otpCode,
      });

      const authData = await authClient.mutate(CREATE_TOKEN_MUTATION, {
        loginMeta: {
          method: 'password',
          ua: window.navigator.userAgent,
        },
      });

      const tokenData = authData.createToken;
      if (!tokenData) {
        throw new Error('Authentication failed');
      }

      await processAuthenticatedUser(tokenData.token);
    } catch (err: unknown) {
      const credentialsMissing = handleCredentialsMissing(err);
      let errorMessage: string;

      if (credentialsMissing) {
        errorMessage = credentialsMissing;
      } else if (err instanceof UnauthorizedError) {
        switch (err.code) {
          case ERROR_CODES.AUTH_OTP_INVALID:
            errorMessage = 'Invalid verification code. Please try again.';
            break;
          case ERROR_CODES.AUTH_LOCKED_FOR_INTERVAL:
            errorMessage = 'Too many attempts. Please try again later.';
            break;
          default:
            Sentry.captureException(err);
            errorMessage = err.message || 'Verification failed. Please try again.';
        }
      } else if (err instanceof ApolloError) {
        Sentry.captureException(err);
        errorMessage = 'Service temporarily unavailable. Please try again.';
      } else if (err instanceof APIError) {
        Sentry.captureException(err);
        errorMessage = apiErrorMessage(err, 'An unexpected error occurred.');
      } else if (err instanceof Error) {
        Sentry.captureException(err);
        errorMessage = err.message || 'An unexpected error occurred.';
      } else {
        Sentry.captureException(err);
        errorMessage = 'An unexpected error occurred.';
      }

      setError(errorMessage);
      onError(errorMessage);
      setOtpCode('');
    } finally {
      setIsLoading(false);
      clearCache('smb_consultation_data');
    }
  };

  /**
   * Cancel MFA verification and return to email/password form
   */
  const handleMfaCancel = () => {
    setMfa({
      required: false,
      method: null,
      maskedAddress: null,
    });
    setOtpCode('');
    setError(null);
    setMfaResendSuccess(false);
  };

  /**
   * Resend MFA code by re-triggering initial login
   */
  const handleMfaResend = async () => {
    setIsLoading(true);
    setError(null);
    setOtpCode('');
    setMfaResendSuccess(false);

    try {
      const authClient = new GraphQLClient(config.apiUrl, basicAuth(email, password));
      await authClient.mutate(CREATE_TOKEN_MUTATION, {
        loginMeta: {
          method: 'password',
          ua: window.navigator.userAgent,
        },
      });
      // If login succeeds without MFA, something unexpected happened
      console.warn('[Email Login] Unexpected success on MFA resend');
    } catch (err: unknown) {
      if (err instanceof UnauthorizedError && err.code === ERROR_CODES.AUTH_MFA_REQUIRED) {
        // Expected - code was resent successfully
        setError(null);
        setMfaResendSuccess(true);
      } else {
        Sentry.captureException(err);
        setError('Failed to resend code. Please try again.');
        onError('Failed to resend code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    error,
    isLoading,
    handleLogin,
    // MFA state and handlers
    mfa,
    otpCode,
    setOtpCode,
    handleMfaVerify,
    handleMfaCancel,
    handleMfaResend,
    mfaResendSuccess,
  };
};
